import React, { useEffect, ChangeEvent, FormEvent, useReducer, useState } from 'react';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../../../components/bearing-search/reducers/reducer';
import TextInput from '../../../../components/bearing-search/components/forms/TextInput';
import PartSearch from '../../../../components/bearing-search/components/PartSearch';
import { greaseFillSpecTypes, text, unitList } from '../../../data';
import {
	BearingInfo,
	TimkenGreaseSpecificationType,
	TimkenUnit,
} from '../../../../components/bearing-search/types/types';
import SelectDropdown from '../../../../components/bearing-search/components/forms/SelectDropdown';
import greaseFillByPartReducer, {
	initialFillByPartNumberState,
} from './reducer';
import { bearingSearchUrl } from '../../../../components/bearing-search/api/api';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import { GreaseQuantityPartPayload } from '../InitialGreaseFill';
import { getPercentageErrorMessage, isValidRange } from '../../../utils/validations';
import {
	updateToolParams,
	getPartNumberFromUrl,
	getPartStateFromParams,
} from '../../../utils/engineering-tools';
import PartPreSearch from '../../../components/PartPreSearch';

interface FillByPartNumberProps {
	onSubmit: (
		payload: GreaseQuantityPartPayload,
		bearing?: BearingPartState
	) => void;
	onClear: () => void;
}

const FillByPartNumber: React.FC<FillByPartNumberProps> = ( {
	onSubmit,
	onClear,
} ) => {
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ isValidated, setIsValidated ] = useState( false );
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);

	// Pass down part number to next recommended tool
	useEffect( () => {
		updateToolParams<BearingPartState>( partState );

		return () => {
			updateToolParams();
		};
	}, [ partState.marketPlaceDescription ] );

	const [ formState, dispatch ] = useReducer(
		greaseFillByPartReducer,
		initialFillByPartNumberState,
	);

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
		setIsPartSelected( false );
	};

	const searchPart = ( event: FormEvent ) => {
		event.preventDefault();

		if ( ! partState.partNumber ) {
			setIsPartOpen( true );
		}
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );
			setIsPartSelected( true );
			setSearchInput( part.marketPlaceDescription || '' );
		}

		dispatch( {
			type: 'update_request_data',
		} );

		setIsPartOpen( false );
	};

	const clearSearch = () => {
		setSearchInput( '' );
		setIsValidated( false );
		setIsPartSelected( false );

		partDispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'reset',
			payload: initialFillByPartNumberState,
		} );

		onClear();
	};

	const handleFormChange = () => {
		setIsValidated( false );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const handleCalculate = ( event: FormEvent ) => {
		event.preventDefault();

		setIsValidated( true );

		if ( partState.partNumber && formState.requestData ) {
			const {
				bearingType,
				bearingSubType,
				partNumber,
				cupPartNumber,
				bore: innerDiameter,
				outerDiameter,
				bearingWidth,
				bearingWeight,
			} = partState;

			const { requestData, unit } = formState;

			onSubmit(
				{
					...requestData,
					unit,
					bearingType,
					bearingSubType,
					partNumber,
					cupPartNumber,
					innerDiameter,
					outerDiameter,
					bearingWidth,
					bearingWeight,
				},
				partState,
			);
		}
	};
	useEffect(() => {
		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				setSearchInput(partNumberFromUrl);
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setIsPartSelected( true );
				setSearchInput( savedPart.marketPlaceDescription || '' );
			}
		}
	}, []);

	const preSearchClickHandler = (e: React.MouseEvent<HTMLElement>) => {
		setIsPartSelected(true);
		searchPart(e);
	};

	return (
		<>
			<p className="t-engineering-tools__description">
				{text.dontKnowPartNumber}{' '}
				<a href={bearingSearchUrl}>{text.bearingSearch}</a>
			</p>

			<form onChange={handleFormChange} onSubmit={searchPart}>
				<div className="t-engineering-tools__form-fields t-engineering-tools__form-fields--multi-column timken-tool-presearch-container">
					<TextInput
						id="initial_grease_fill_part_number"
						required={true}
						label={text.partNumber}
						verified={!! partState.partNumber}
						name="partNumber"
						value={searchInput}
						disabled={!! partState.partNumber}
						placeholder={text.partNumber}
						message={
							partState.partNumber
								? `${ text.bearingType }: ${ partState.bearingTypeLabel }`
								: text.partWildCardHint
						}
						onChange={inputPartNumber}
					/>

					<PartPreSearch
						input={searchInput}
						isHidden={searchInput.length < 2 || isPartOpen || isPartSelected}
						unit={formState.unit}
						onClick={(e) => preSearchClickHandler(e)}
						verifyPartNumber={(value: string) => setSearchInput(value)}
					/>

					<SelectDropdown
						id="initial_grease_fill_unit"
						name="unit"
						required={true}
						label={text.displayUnits}
						value={formState.unit}
						options={unitList}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_unit',
								payload: event.target.value as TimkenUnit,
							} )
						}
					/>

					{! partState.partNumber && (
						<button
							className="btn btn--primary"
							type="submit"
							disabled={! searchInput}
						>
							{text.lookup}
						</button>
					)}
				</div>
			</form>

			{!! partState.partNumber && (
				<form onChange={handleFormChange} onSubmit={handleCalculate}>
					<div className="row form-row">
						<div className="col-md-3">
							<SelectDropdown
								id="initial_grease_fill_specs_type"
								required={true}
								label={text.specsType}
								value={formState.specsType}
								options={greaseFillSpecTypes}
								onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
									dispatch( {
										type: 'change_specs_type',
										payload: event.target
											.value as TimkenGreaseSpecificationType,
									} )
								}
							/>
						</div>
					</div>

					<div className="row form-row">
						{formState.specsType === 'Timken' && (
							<div className="col-md-3">
								<NumberInput
									id="initial_grease_fill_speed"
									required={true}
									label={text.speed}
									unitLabel="rpm"
									value={formState.speedDataID}
									min={0}
									invalid={
										isValidated &&
									! isValidRange( formState.speedDataID, 0, 1000000, true )
									}
									errorMessage={text.errorSpeed}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_speed_id',
											payload: event.target.value,
										} )
									}
								/>
							</div>
						)}

						{formState.specsType === 'Custom' && (
							<div className="col-md-3">
								<NumberInput
									id="initial_grease_fill_percentage"
									required={true}
									label={text.percentageFill}
									unitLabel="%"
									value={formState.percentageFillData}
									invalid={
										isValidated &&
										formState.specsType === 'Custom' &&
										! isValidRange( formState.percentageFillData, 0, 100, true )
									}
									errorMessage={getPercentageErrorMessage( formState.percentageFillData )}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_percentage_fill',
											payload: event.target.value,
										} )
									}
								/>
							</div>
						)}
					</div>

					<div>
						<div className="t-engineering-tools__terms-of-use"
							  dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
						/>
						<div className="t-engineering-tools__search-actions">
							<button
								className="btn btn--primary"
								type="submit"
								disabled={isValidated}
							>
								{text.calculate}
							</button>

							<button
								className="btn btn--secondary"
								type="button"
								onClick={() => clearSearch()}
							>
								{text.clear}
							</button>
						</div>
					</div>
				</form>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch
					units={formState.unit}
					partNumber={searchInput}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
				/>
			)}
		</>
	);
};

export default FillByPartNumber;
